/**
 * Tenant-scoped Storage paths for NEW uploads: `{ns}/{tenantId}/{...segments}`.
 *
 * Reads/deletes of existing files must keep using the `storagePath`/`pdfUrl`
 * field stored on the Firestore doc — pre-migration files are NOT moved and
 * stay readable via the legacy storage.rules blocks.
 */

import type { Namespace } from './collections';
import { getTenantId } from './tenant';

export function storagePath(ns: Namespace, ...segments: string[]): string {
  const tenantId = getTenantId();
  if (tenantId === null) {
    throw new Error('No tenant resolved for this session');
  }
  return [ns, tenantId, ...segments].join('/');
}

/**
 * Path of the thumbnail the `storage-resize-images` extension writes beside an
 * uploaded image: `dir/photo.jpg` → `dir/photo_200x200.webp`.
 *
 * The suffix mirrors the deployed extension config (`IMG_SIZES=200x200`,
 * `IMAGE_TYPE=webp`, no `resizedImagesPath`, so it lands in the same folder) —
 * see extensions/storage-resize-images.env. Change one and this must follow.
 *
 * This is the OBJECT path, not a URL: the thumbnail is a separate Storage
 * object with its own download token, so a display URL still requires a
 * getDownloadURL on this path. It cannot be string-derived from the original's
 * download URL.
 */
export function thumbnailPath(objectPath: string): string {
  const ext = objectPath.match(/\.[^./]+$/)?.[0] ?? '';
  return `${objectPath.slice(0, objectPath.length - ext.length)}_200x200.webp`;
}

/**
 * Object path out of a Firebase Storage download URL, i.e. the url-encoded
 * segment after `/o/`: `https://…/o/elka%2Facme%2Fx.jpg?alt=media&token=…`
 * → `elka/acme/x.jpg`. Returns null for anything that isn't one.
 *
 * Needed because only the download URL is persisted on the Firestore doc
 * (`imageUrl`), while addressing the resized sibling requires the path.
 */
export function storagePathFromUrl(downloadUrl: string): string | null {
  try {
    const match = new URL(downloadUrl).pathname.match(/\/o\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}
